import React, { useRef, useEffect, useState } from 'react';
import { TYPOGRAPHY } from './utils/constants';

interface GridPingsProps {
  maxPings?: number;
  spawnInterval?: number;
  baseOpacity?: number;
}

interface Ping {
  x: number;
  y: number;
  startTime: number;
  duration: number;
  dataFragment: string;
}

const GRID_SIZE = 80;

const DATA_FRAGMENTS = [
  '0.83', '+268 bp', '726169', 'ACK', '99.7%',
  'CONF', '+1.4\u03C3', '0x3F2A', 'VALID', '847ms',
  '\u0394+0.12', 'R\u00B2=0.94', 'p<.001', '512K', 'OK:200',
];

function computeGridIntersections(width: number, height: number): Array<{ x: number; y: number }> {
  const intersections: Array<{ x: number; y: number }> = [];
  for (let x = GRID_SIZE; x < width; x += GRID_SIZE) {
    for (let y = GRID_SIZE; y < height; y += GRID_SIZE) {
      intersections.push({ x, y });
    }
  }
  return intersections;
}

function drawPing(
  ctx: CanvasRenderingContext2D,
  ping: Ping,
  progress: number,
  baseOpacity: number,
) {
  const opacity = baseOpacity * (1 - progress);
  const radius = 2 + progress * 3;

  // Soft glow (intersection-strength)
  const gradient = ctx.createRadialGradient(ping.x, ping.y, 0, ping.x, ping.y, radius * 5);
  gradient.addColorStop(0, `rgba(201, 162, 39, ${opacity * 0.35})`);
  gradient.addColorStop(1, 'rgba(201, 162, 39, 0)');
  ctx.beginPath();
  ctx.arc(ping.x, ping.y, radius * 5, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();

  // Core dot (intersection-strength)
  ctx.beginPath();
  ctx.arc(ping.x, ping.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(201, 162, 39, ${opacity})`;
  ctx.fill();

  // Floating data fragment
  if (progress < 0.8) {
    const textOpacity = baseOpacity * 0.5 * (1 - progress / 0.8);
    const yOffset = -10 - progress * 18;
    ctx.font = `500 9px ${TYPOGRAPHY.fontFamily.mono}`;
    ctx.fillStyle = `rgba(201, 162, 39, ${textOpacity})`;
    ctx.textAlign = 'left';
    ctx.fillText(ping.dataFragment, ping.x + 8, ping.y + yOffset);
  }
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/* One static frame: a deterministic sample of intersections, drawn mid-fade
   so the grid still reads as instrumented without anything moving. */
function drawStaticFrame(
  ctx: CanvasRenderingContext2D,
  intersections: Array<{ x: number; y: number }>,
  maxPings: number,
  baseOpacity: number,
) {
  if (intersections.length === 0) return;
  const step = Math.max(1, Math.floor(intersections.length / (maxPings + 1)));
  for (let i = 0; i < maxPings; i += 1) {
    const pt = intersections[Math.min((i + 1) * step, intersections.length - 1)];
    drawPing(
      ctx,
      {
        x: pt.x,
        y: pt.y,
        startTime: 0,
        duration: 1,
        dataFragment: DATA_FRAGMENTS[i % DATA_FRAGMENTS.length],
      },
      0.35,
      baseOpacity,
    );
  }
}

const GridPings: React.FC<GridPingsProps> = ({
  maxPings = 3,
  spawnInterval = 1400,
  baseOpacity = 0.7,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const pingsRef = useRef<Ping[]>([]);
  const lastSpawnRef = useRef<number>(0);
  const intersectionsRef = useRef<Array<{ x: number; y: number }>>([]);
  const [reducedMotion, setReducedMotion] = useState<boolean>(prefersReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = window.devicePixelRatio || 1;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      intersectionsRef.current = computeGridIntersections(w, h);
    };

    if (reducedMotion) {
      const renderStatic = () => {
        resize();
        const parent = canvas.parentElement;
        if (!parent) return;
        ctx.clearRect(0, 0, parent.clientWidth, parent.clientHeight);
        drawStaticFrame(ctx, intersectionsRef.current, maxPings, baseOpacity);
      };
      renderStatic();
      window.addEventListener('resize', renderStatic);
      return () => window.removeEventListener('resize', renderStatic);
    }

    resize();
    window.addEventListener('resize', resize);

    const loop = (now: number) => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const w = parent.clientWidth;
      const h = parent.clientHeight;

      ctx.clearRect(0, 0, w, h);

      // Spawn new ping
      if (
        pingsRef.current.length < maxPings &&
        now - lastSpawnRef.current > spawnInterval &&
        intersectionsRef.current.length > 0
      ) {
        const idx = Math.floor(Math.random() * intersectionsRef.current.length);
        const pt = intersectionsRef.current[idx];
        pingsRef.current.push({
          x: pt.x,
          y: pt.y,
          startTime: now,
          duration: 900 + Math.random() * 600,
          dataFragment: DATA_FRAGMENTS[Math.floor(Math.random() * DATA_FRAGMENTS.length)],
        });
        lastSpawnRef.current = now;
      }

      // Draw active pings
      const alive: Ping[] = [];
      for (const ping of pingsRef.current) {
        const progress = (now - ping.startTime) / ping.duration;
        if (progress > 1) continue;
        drawPing(ctx, ping, progress, baseOpacity);
        alive.push(ping);
      }
      pingsRef.current = alive;

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [maxPings, spawnInterval, baseOpacity, reducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default GridPings;
